import { useForm, usePage, Link, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { StageChip } from "@/components/StageChip"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FlashMessages } from "@/components/FlashMessages"
import { AssetForm } from "@/components/AssetForm"
import { AssetTimeline } from "@/components/AssetTimeline"
import { MoveStageDialog } from "@/components/MoveStageDialog"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ArrowLeft, ArrowRightLeft, Package, ExternalLink, History, Save, Trash2, QrCode, ChevronsUpDown } from "lucide-react"
import { emptyAssetFormData } from "@/types/inventory"
import type { AssetFormData, InventoryShowPageProps } from "@/types/inventory"

export default function InventoryShow() {
  const { t } = useTranslation()
  const {
    auth,
    flash,
    asset,
    events,
    assetType,
    stages,
    customers,
    suppliers,
    locations,
    tags,
    products,
    containers,
  } = usePage<InventoryShowPageProps>().props

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deleteConfirmCode, setDeleteConfirmCode] = useState("")
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false)

  const form = useForm<AssetFormData>({
    ...emptyAssetFormData,
    code: asset.code,
    stage_id: asset.stage.id,
    customer_id: asset.customer?.id || "",
    supplier_id: asset.supplier?.id || "",
    location_id: asset.location?.id || "",
    tag_ids: asset.tags.map((t: { id: string }) => t.id),
    notes: asset.notes || "",
    // Keg
    product_id: asset.product?.id || "",
    container_id: asset.container?.id || "",
    bought_at: asset.bought_at?.split("T")[0] || new Date().toISOString().split("T")[0],
    serial_number: asset.serial_number || "",
    batch_number: asset.batch_number || "",
    expiration_date: asset.expiration_date?.split("T")[0] || "",
    tare: asset.tare?.toString() || "",
    volume: asset.volume?.toString() || "",
    // Fridge / BeerTap
    brand: asset.brand || "",
    model: asset.model || "",
    capacity: asset.capacity?.toString() || "",
    // Cylinder
    pressure_rating: asset.pressure_rating?.toString() || "",
    last_inspection_date: asset.last_inspection_date?.split("T")[0] || "",
    expiry_date: asset.expiry_date?.split("T")[0] || "",
    // BeerTap
    tap_count: asset.tap_count?.toString() || "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.patch(`/inventory/${asset.id}?asset_type=${assetType}`, {
      preserveScroll: true,
    })
  }

  function handleDelete() {
    router.delete(`/inventory/${asset.id}`)
  }

  function openDeleteDialog() {
    setDeleteConfirmCode("")
    setIsDeleteDialogOpen(true)
  }

  const canDelete = deleteConfirmCode === asset.code

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
          <div className="flex items-start gap-3.5">
            <Button variant="outline" size="icon" asChild className="h-9 w-9 mt-1.5">
              <Link href={`/inventory?asset_type=${assetType}`}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div>
              <div className="eyebrow mb-1.5">{t("inventory.show.title")}</div>
              <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-none flex items-center gap-3 flex-wrap">
                <span className="font-mono">{asset.code}</span>
                <StageChip stage={asset.stage} />
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setIsMoveDialogOpen(true)}>
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              {t("inventory.moveStage")}
            </Button>
            <Button variant="destructive" onClick={openDeleteDialog}>
              <Trash2 className="w-4 h-4 mr-2" />
              {t("inventory.deleteAsset")}
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("inventory.deleteAsset")}</DialogTitle>
              <DialogDescription>
                {t("inventory.deleteConfirmDescription", { code: asset.code })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="delete-confirm-code">
                  {t("inventory.deleteConfirmLabel")}
                </Label>
                <Input
                  id="delete-confirm-code"
                  value={deleteConfirmCode}
                  onChange={(e) => setDeleteConfirmCode(e.target.value)}
                  placeholder={asset.code}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={!canDelete}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t("common.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-5 items-start">
          {/* Left: Details Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display tracking-tight">
                <span className="grid place-items-center w-7 h-7 rounded-md" style={{ background: "color-mix(in oklab, var(--accent) 15%, transparent)", color: "var(--accent)" }}>
                  <Package className="w-4 h-4" />
                </span>
                {t("inventory.show.details")}
              </CardTitle>
              <CardDescription>{t("inventory.show.detailsDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit}>
                <AssetForm
                  data={form.data}
                  errors={form.errors}
                  onChange={(field, value) => form.setData(field, value)}
                  idPrefix="edit"
                  assetType={assetType}
                  stages={stages}
                  customers={customers}
                  suppliers={suppliers}
                  locations={locations}
                  tags={tags}
                  products={products}
                  containers={containers}
                  disabledFields={["stage_id", "product_id", "customer_id"]}
                />
                <div className="flex justify-end pt-4">
                  <Button type="submit" disabled={form.processing}>
                    <Save className="w-4 h-4 mr-2" />
                    {t("common.save")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Right column */}
          <div className="space-y-6">
          {/* QR Code */}
          {asset.qr_code_url && (
            <Collapsible defaultOpen={false}>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 font-display tracking-tight">
                      <span className="grid place-items-center w-7 h-7 rounded-md" style={{ background: "color-mix(in oklab, var(--accent) 15%, transparent)", color: "var(--accent)" }}>
                        <QrCode className="w-4 h-4" />
                      </span>
                      {t("inventory.qr_codes.qrCode")}
                    </CardTitle>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <ChevronsUpDown className="h-4 w-4" />
                        <span className="sr-only">Toggle</span>
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CardDescription>{t("inventory.qr_codes.subtitle", { defaultValue: "Cole no ativo para escaneamento" })}</CardDescription>
                </CardHeader>
                <CollapsibleContent>
                  <CardContent>
                    <a
                      href={asset.qr_code_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img
                        src={asset.qr_code_url}
                        alt={`QR Code - ${asset.code}`}
                        className="w-full h-auto rounded-md"
                        style={{ border: "1px solid var(--border)" }}
                      />
                    </a>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 font-display tracking-tight">
                  <span className="grid place-items-center w-7 h-7 rounded-md" style={{ background: "color-mix(in oklab, var(--accent) 15%, transparent)", color: "var(--accent)" }}>
                    <History className="w-4 h-4" />
                  </span>
                  {t("inventory.show.timeline")}
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link href={`/inventory/${asset.id}/events`}>
                    {t("inventory.events.viewAll")}
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
              <CardDescription>
                {t("inventory.show.timelineSummary", {
                  defaultValue: "{{count}} eventos",
                  count: events.length,
                })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AssetTimeline events={events} />
            </CardContent>
          </Card>
          </div>
        </div>
      </div>

      <MoveStageDialog
        open={isMoveDialogOpen}
        onOpenChange={setIsMoveDialogOpen}
        assets={[asset]}
        assetType={assetType}
        stages={stages}
        products={products || []}
        customers={customers}
        tags={tags}
      />
    </AppLayout>
  )
}
